import {
  unwrapFieldValue,
  wrapFieldValue,
} from 'features/libra/ontology/ontologyFieldDefaults'
import type { FieldValues } from 'features/libra/ontology/ontologyFieldTypes'

/**
 * The single `fieldValues` key holding every justification history on a
 * ticket, keyed by the `justify.id` of the transition that produced it.
 */
export const TRANSITION_JUSTIFICATIONS_FIELD = 'transition_justifications'

/**
 * One archived decision. Self-describing, so every justified transition on a
 * ticket can share a single history.
 */
export interface JustificationRecord {
  text: string
  /** User id, so the record survives a display-name change. */
  justifiedById: string
  /** Display name, falling back to email. */
  justifiedBy: string
  /** ISO timestamp of the transition. */
  justifiedAt: string
  /** The action's label, e.g. "Return to In Progress". */
  action: string
  fromStage: string
  toStage: string
}

type TransitionJustifications = Record<string, JustificationRecord[]>

function readAll(fieldValues: FieldValues): TransitionJustifications {
  return (
    unwrapFieldValue<TransitionJustifications>(
      fieldValues[TRANSITION_JUSTIFICATIONS_FIELD]
    ) ?? {}
  )
}

/** Oldest first. Empty until the first justified transition for that id. */
export function readJustifications(
  fieldValues: FieldValues,
  id: string
): JustificationRecord[] {
  return readAll(fieldValues)[id] ?? []
}

export function appendJustification(
  fieldValues: FieldValues,
  id: string,
  record: JustificationRecord
): FieldValues {
  return {
    [TRANSITION_JUSTIFICATIONS_FIELD]: wrapFieldValue({
      ...readAll(fieldValues),
      [id]: [...readJustifications(fieldValues, id), record],
    }),
  }
}
